import { clsx } from 'clsx';
import { forwardRef, type AnchorHTMLAttributes, type ButtonHTMLAttributes } from 'react';

type Variant = 'primary' | 'secondary' | 'ghost' | 'danger';
type Size = 'sm' | 'md' | 'lg';

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: Variant;
  size?: Size;
  loading?: boolean;
}

const variants: Record<Variant, string> = {
  primary: 'bg-brand-600 text-white hover:bg-brand-700 active:bg-brand-700 disabled:bg-brand-600/50',
  secondary:
    'bg-surface-sunken text-content ring-1 ring-inset ring-line hover:bg-line/40 active:bg-line/60',
  ghost: 'text-content-muted hover:bg-surface-sunken active:bg-line/50',
  danger: 'bg-red-600 text-white hover:bg-red-700 active:bg-red-800',
};

// Pill-shaped at every size, with side padding scaled to the radius so labels
// keep clear of the curve.
const sizes: Record<Size, string> = {
  sm: 'h-8 px-3.5 text-sm rounded-full',
  md: 'h-10 px-5 text-sm rounded-full',
  lg: 'h-12 px-6 text-base rounded-full',
};

// Forwards its ref so callers can focus it — e.g. ConfirmDialog putting the
// initial focus on Cancel rather than on the destructive action.
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant = 'primary',
    size = 'md',
    // HTML defaults a <button> inside a <form> to type="submit". That default
    // is almost never what a caller means: one unlabelled button in a form
    // silently submits it. Submitting is opt-in here.
    type = 'button',
    loading,
    disabled,
    className,
    children,
    ...rest
  },
  ref,
) {
  return (
    <button
      {...rest}
      ref={ref}
      type={type}
      disabled={disabled || loading}
      className={clsx(
        'inline-flex items-center justify-center gap-2 font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
        'disabled:cursor-not-allowed',
        variants[variant],
        sizes[size],
        className,
      )}
    >
      {loading ? <span className="h-2 w-2 animate-pulse rounded-full bg-current" /> : null}
      {children}
    </button>
  );
});

/**
 * Un enlace con el aspecto de un botón.
 *
 * Hermano de `Button` y no una prop `as` suya: lo que hacen es distinto —éste
 * navega, aquél ejecuta— y un `<a>` no admite `disabled` ni `type`. Comparte
 * las tablas de estilo, así que ninguno de los dos se queda atrás cuando la
 * otra cambia.
 *
 * Para `tel:`, `mailto:` y enlaces externos, que es donde una acción de la
 * interfaz sale del navegador.
 */
export function ButtonLink({
  variant = 'primary',
  size = 'md',
  className,
  children,
  ...rest
}: AnchorHTMLAttributes<HTMLAnchorElement> & { variant?: Variant; size?: Size }) {
  return (
    <a
      {...rest}
      className={clsx(
        'inline-flex items-center justify-center gap-2 font-medium transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2 focus-visible:ring-offset-surface',
        variants[variant],
        sizes[size],
        className,
      )}
    >
      {children}
    </a>
  );
}
